import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Building2, Shield, AlertTriangle, CheckCircle, XCircle, Clock,
  Target, RefreshCw, Play, TrendingUp, TrendingDown, Minus,
  AlertCircle, Gauge, BarChart3, Users, Layers, List, Eye,
  Plus, ChevronRight, Activity, Zap, Flag, FileText, Calendar,
  PieChart, Grid3X3, ArrowUp, ArrowDown, Repeat, Search
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';

export default function EnterpriseAuditPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [findings, setFindings] = useState([]);
  const [repeatAnalysis, setRepeatAnalysis] = useState(null);
  const [deptComparison, setDeptComparison] = useState(null);
  
  // Dialogs
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Campaign form
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    campaign_type: 'internal',
    standard_ids: [],
    scope_departments: [],
    is_enterprise_wide: false,
    planned_start: null,
    planned_end: null
  });

  // Available standards
  const standards = [
    { id: 'gaca_part5', name: 'GACA Part 5 SMS', short: 'GACA' },
    { id: 'easa_oro_gen', name: 'EASA ORO.GEN.200', short: 'EASA' },
    { id: 'faa_part5', name: 'FAA Part 5', short: 'FAA' },
    { id: 'iosa', name: 'IOSA Standards', short: 'IOSA' },
    { id: 'internal', name: 'Internal SMS', short: 'INT' }
  ];

  const departments = ['Flight Operations', 'Maintenance', 'Ground Handling', 'Training', 'Quality Assurance', 'Safety'];

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get('/enterprise-audit/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  }, [api]);

  // Fetch readiness
  const fetchReadiness = useCallback(async () => {
    try {
      const response = await api.get('/enterprise-audit/readiness');
      setReadiness(response.data);
    } catch (error) {
      console.error('Failed to fetch readiness:', error);
    }
  }, [api]);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await api.get('/enterprise-audit/campaigns');
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  }, [api]);

  // Fetch findings
  const fetchFindings = useCallback(async () => {
    try {
      const response = await api.get('/enterprise-audit/findings?limit=50');
      setFindings(response.data.findings || []);
    } catch (error) {
      console.error('Failed to fetch findings:', error);
    }
  }, [api]);

  // Fetch repeat analysis
  const fetchRepeatAnalysis = useCallback(async () => {
    try {
      const response = await api.get('/enterprise-audit/analysis/repeat-findings');
      setRepeatAnalysis(response.data);
    } catch (error) {
      console.error('Failed to fetch repeat analysis:', error);
    }
  }, [api]);

  // Fetch department comparison
  const fetchDeptComparison = useCallback(async () => {
    try {
      const response = await api.get('/enterprise-audit/analysis/department-comparison');
      setDeptComparison(response.data);
    } catch (error) {
      console.error('Failed to fetch comparison:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchDashboard();
    fetchReadiness();
    fetchCampaigns();
    fetchFindings();
    fetchRepeatAnalysis();
    fetchDeptComparison();
  }, [fetchDashboard, fetchReadiness, fetchCampaigns, fetchFindings, fetchRepeatAnalysis, fetchDeptComparison]);

  // Create campaign
  const handleCreateCampaign = async () => {
    if (!campaignForm.name) {
      toast.error('Please enter campaign name');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/enterprise-audit/campaigns', {
        ...campaignForm,
        campaign_lead_id: 'current_user', // Would be actual user ID
        planned_start: campaignForm.planned_start?.toISOString() || new Date().toISOString(),
        planned_end: campaignForm.planned_end?.toISOString() || new Date(Date.now() + 30*24*60*60*1000).toISOString()
      });
      toast.success('Campaign created');
      setShowCreateCampaign(false);
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to create campaign');
    }
    setLoading(false);
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500',
      major: 'bg-orange-500',
      minor: 'bg-amber-500',
      observation: 'bg-blue-500'
    };
    return colors[severity] || 'bg-slate-500';
  };

  // Get heat color
  const getHeatColor = (level) => {
    const colors = {
      critical: 'bg-red-600',
      high: 'bg-orange-500',
      medium: 'bg-amber-500',
      low: 'bg-emerald-500'
    };
    return colors[level] || 'bg-slate-500';
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge className="bg-red-600"><AlertCircle className="w-3 h-3 mr-1" />Open</Badge>;
      case 'capa_assigned':
        return <Badge className="bg-amber-600"><Clock className="w-3 h-3 mr-1" />CAPA Assigned</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-600"><Play className="w-3 h-3 mr-1" />In Progress</Badge>;
      case 'closed':
        return <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Closed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Chart colors
  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

  return (
    <div className={cn("space-y-6 p-6", isDark ? "bg-slate-950" : "bg-gray-50")} data-testid="enterprise-audit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-2xl font-bold flex items-center gap-3", isDark ? "text-white" : "text-gray-900")}>
            <Building2 className="w-8 h-8 text-emerald-500" />
            Enterprise Audit Management
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
            Consolidated view of all audits, findings, and compliance status
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => {
              fetchDashboard();
              fetchReadiness();
            }}
            variant="outline"
            className={cn(isDark ? "border-slate-600" : "border-gray-300")}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowCreateCampaign(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Compliance Score</p>
                  <p className={cn(
                    "text-3xl font-bold",
                    dashboard.overall_compliance_score >= 80 ? "text-emerald-400" :
                    dashboard.overall_compliance_score >= 60 ? "text-amber-400" : "text-red-400"
                  )}>
                    {dashboard.overall_compliance_score?.toFixed(0) || 0}%
                  </p>
                </div>
                <div className="p-3 bg-emerald-900/30 rounded-lg">
                  <Gauge className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Active Campaigns</p>
                  <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                    {dashboard.active_campaigns}
                  </p>
                </div>
                <div className="p-3 bg-blue-900/30 rounded-lg">
                  <Layers className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Open Findings</p>
                  <p className={cn("text-3xl font-bold", dashboard.open_findings > 0 ? "text-amber-400" : "text-emerald-400")}>
                    {dashboard.open_findings}
                  </p>
                </div>
                <div className={cn("p-3 rounded-lg", dashboard.open_findings > 0 ? "bg-amber-900/30" : "bg-emerald-900/30")}>
                  <AlertTriangle className={cn("w-6 h-6", dashboard.open_findings > 0 ? "text-amber-400" : "text-emerald-400")} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(
            dashboard.overdue_capas > 0 ? "border-red-500/50" : "",
            isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200"
          )}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Overdue CAPAs</p>
                  <p className={cn("text-3xl font-bold", dashboard.overdue_capas > 0 ? "text-red-400" : "text-emerald-400")}>
                    {dashboard.overdue_capas}
                  </p>
                </div>
                <div className={cn("p-3 rounded-lg", dashboard.overdue_capas > 0 ? "bg-red-900/30" : "bg-emerald-900/30")}>
                  {dashboard.overdue_capas > 0 ? (
                    <XCircle className="w-6 h-6 text-red-400" />
                  ) : (
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Findings YTD</p>
                  <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                    {dashboard.total_findings_ytd}
                  </p>
                </div>
                <div className="p-3 bg-purple-900/30 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "bg-gray-100 border-gray-200")}>
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Layers className="w-4 h-4 mr-2" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="findings" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Findings
          </TabsTrigger>
          <TabsTrigger value="heatmap" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Activity className="w-4 h-4 mr-2" />
            Heatmap
          </TabsTrigger>
          <TabsTrigger value="readiness" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Target className="w-4 h-4 mr-2" />
            Readiness
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Standards Compliance */}
            <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Shield className="w-5 h-5 text-emerald-400" />
                  Standards Compliance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {dashboard?.standards_compliance?.map((std, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-gray-900")}>
                          {std.standard_name}
                        </span>
                        <span className={cn(
                          "text-sm font-bold",
                          std.compliance_percentage >= 80 ? "text-emerald-400" :
                          std.compliance_percentage >= 60 ? "text-amber-400" : "text-red-400"
                        )}>
                          {std.compliance_percentage?.toFixed(0)}%
                        </span>
                      </div>
                      <Progress value={std.compliance_percentage} className="h-2" />
                      <div className={cn("flex justify-between text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                        <span>{std.audits_count} audits</span>
                        <span>{std.open_findings} open findings</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Department Performance */}
            <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Users className="w-5 h-5 text-blue-400" />
                  Department Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deptComparison?.departments && (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={deptComparison.departments.slice(0, 5)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
                        <XAxis type="number" domain={[0, 100]} stroke={isDark ? "#64748b" : "#9ca3af"} />
                        <YAxis dataKey="department" type="category" width={100} stroke={isDark ? "#64748b" : "#9ca3af"} fontSize={11} />
                        <RechartsTooltip 
                          contentStyle={{ 
                            backgroundColor: isDark ? '#1e293b' : '#fff', 
                            border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="compliance_score" fill="#10b981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Findings */}
            <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Recent Findings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px]">
                  <div className="space-y-3">
                    {dashboard?.recent_findings?.slice(0, 5).map((finding, idx) => (
                      <div 
                        key={idx}
                        className={cn(
                          "p-3 rounded-lg border",
                          isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={getSeverityColor(finding.severity)}>{finding.severity}</Badge>
                              <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                                {finding.finding_number}
                              </span>
                            </div>
                            <p className={cn("text-sm font-medium line-clamp-1", isDark ? "text-white" : "text-gray-900")}>
                              {finding.title}
                            </p>
                            <p className={cn("text-xs mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
                              {finding.department}
                            </p>
                          </div>
                          {getStatusBadge(finding.status)}
                        </div>
                      </div>
                    ))}
                    {(!dashboard?.recent_findings || dashboard.recent_findings.length === 0) && (
                      <p className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
                        No recent findings
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Systemic Weaknesses */}
            <Card className={cn(
              dashboard?.systemic_weaknesses?.length > 0 ? "border-amber-500/50" : "",
              isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200"
            )}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Repeat className="w-5 h-5 text-red-400" />
                  Systemic Weaknesses
                </CardTitle>
                <CardDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
                  Repeat findings and patterns requiring attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-3">
                    {dashboard?.systemic_weaknesses?.map((weakness, idx) => (
                      <div 
                        key={idx}
                        className={cn(
                          "p-3 rounded-lg border",
                          weakness.risk_level === 'high' ? "border-red-500/50 bg-red-900/20" : "border-amber-500/50 bg-amber-900/20"
                        )}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <Badge className={weakness.risk_level === 'high' ? 'bg-red-600' : 'bg-amber-600'}>
                            {weakness.risk_level?.toUpperCase()} RISK
                          </Badge>
                          <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                            {weakness.occurrence_count} occurrences
                          </span>
                        </div>
                        <p className={cn("text-sm", isDark ? "text-white" : "text-gray-900")}>
                          {weakness.description}
                        </p>
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {weakness.affected_departments?.slice(0, 3).map((dept, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{dept}</Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                    {(!dashboard?.systemic_weaknesses || dashboard.systemic_weaknesses.length === 0) && (
                      <div className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
                        <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                        <p>No systemic weaknesses detected</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                <Layers className="w-5 h-5 text-emerald-400" />
                Audit Campaigns
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {campaigns.map(campaign => (
                  <div 
                    key={campaign.id}
                    className={cn(
                      "p-4 rounded-lg border cursor-pointer transition-colors",
                      campaign.status === 'in_progress' && "border-blue-500/50",
                      isDark ? "bg-slate-900/50 border-slate-700 hover:border-slate-600" : "bg-gray-50 border-gray-200 hover:border-gray-300"
                    )}
                    onClick={() => setSelectedCampaign(campaign)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                            {campaign.name}
                          </h3>
                          <Badge className={
                            campaign.status === 'in_progress' ? 'bg-blue-600' :
                            campaign.status === 'completed' ? 'bg-emerald-600' :
                            campaign.status === 'planning' ? 'bg-amber-600' : 'bg-slate-600'
                          }>
                            {campaign.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {campaign.planned_start && format(new Date(campaign.planned_start), 'MMM d')} - {campaign.planned_end && format(new Date(campaign.planned_end), 'MMM d, yyyy')}
                          </span>
                          <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                            <Users className="w-3 h-3 inline mr-1" />
                            {campaign.total_auditors || 0} auditors
                          </span>
                        </div>
                        <div className="flex gap-1 mt-2">
                          {campaign.standard_ids?.map((std, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {standards.find(s => s.id === std)?.short || std}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                          {campaign.completed_audits || 0}/{campaign.total_audits || 0}
                        </div>
                        <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Audits</p>
                        {campaign.total_findings > 0 && (
                          <p className="text-xs text-amber-400 mt-1">
                            {campaign.total_findings} findings
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {campaigns.length === 0 && (
                  <div className={cn("text-center py-12", isDark ? "text-slate-500" : "text-gray-400")}>
                    <Layers className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No campaigns yet</p>
                    <p className="text-sm mt-1">Create your first audit campaign</p>
                    <Button 
                      className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setShowCreateCampaign(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Campaign
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Findings Tab */}
        <TabsContent value="findings" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  All Findings
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-red-600">{findings.filter(f => f.severity === 'critical').length} Critical</Badge>
                  <Badge className="bg-orange-600">{findings.filter(f => f.severity === 'major').length} Major</Badge>
                  <Badge className="bg-amber-600">{findings.filter(f => f.severity === 'minor').length} Minor</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {findings.map(finding => (
                    <div 
                      key={finding.id}
                      className={cn(
                        "p-4 rounded-lg border",
                        finding.severity === 'critical' && "border-red-500/50",
                        isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getSeverityColor(finding.severity)}>{finding.severity}</Badge>
                            <span className={cn("text-sm font-mono", isDark ? "text-slate-300" : "text-gray-700")}>
                              {finding.finding_number}
                            </span>
                            {finding.is_repeat_finding && (
                              <Badge variant="outline" className="text-xs border-red-500 text-red-400">
                                <Repeat className="w-3 h-3 mr-1" />
                                Repeat
                              </Badge>
                            )}
                          </div>
                          <h4 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                            {finding.title}
                          </h4>
                          <p className={cn("text-sm mt-1 line-clamp-2", isDark ? "text-slate-400" : "text-gray-600")}>
                            {finding.description}
                          </p>
                          <div className={cn("flex items-center gap-4 mt-2 text-xs", isDark ? "text-slate-500" : "text-gray-400")}>
                            <span>{finding.department}</span>
                            <span>{finding.clause_code}</span>
                            <span>{finding.raised_at && format(new Date(finding.raised_at), 'MMM d, yyyy')}</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          {getStatusBadge(finding.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                  {findings.length === 0 && (
                    <div className={cn("text-center py-12", isDark ? "text-slate-500" : "text-gray-400")}>
                      <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                      <p className="text-lg font-medium">No findings recorded</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Heatmap Tab */}
        <TabsContent value="heatmap" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                <Activity className="w-5 h-5 text-orange-400" />
                Finding Heatmap
              </CardTitle>
              <CardDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
                Finding concentration by department and category
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.finding_heatmap && dashboard.finding_heatmap.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className={cn("text-left p-2 text-sm font-medium", isDark ? "text-slate-400" : "text-gray-500")}>Department</th>
                        {['policy', 'hazard_reporting', 'risk_management', 'safety_assurance', 'training', 'documentation'].map(cat => (
                          <th key={cat} className={cn("text-center p-2 text-xs font-medium capitalize", isDark ? "text-slate-400" : "text-gray-500")}>
                            {cat.replace('_', ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {['Flight Operations', 'Maintenance', 'Ground Handling', 'Training'].map(dept => (
                        <tr key={dept}>
                          <td className={cn("p-2 text-sm", isDark ? "text-white" : "text-gray-900")}>{dept}</td>
                          {['policy', 'hazard_reporting', 'risk_management', 'safety_assurance', 'training', 'documentation'].map(cat => {
                            const cell = dashboard.finding_heatmap.find(h => h.department === dept && h.category === cat);
                            return (
                              <td key={cat} className="p-1">
                                <div 
                                  className={cn(
                                    "w-full h-12 rounded flex items-center justify-center text-white text-xs font-bold",
                                    cell ? getHeatColor(cell.heat_level) : (isDark ? "bg-slate-800" : "bg-gray-100")
                                  )}
                                >
                                  {cell?.total_findings || 0}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-center gap-4 mt-4">
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-emerald-500"></div>
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Low</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-amber-500"></div>
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Medium</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-orange-500"></div>
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>High</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-red-600"></div>
                      <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Critical</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={cn("text-center py-12", isDark ? "text-slate-500" : "text-gray-400")}>
                  <Grid3X3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Run audits to populate the heatmap</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Repeat Findings Analysis */}
          {repeatAnalysis && repeatAnalysis.analysis?.length > 0 && (
            <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Repeat className="w-5 h-5 text-red-400" />
                  Repeat Finding Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {repeatAnalysis.analysis.slice(0, 5).map((item, idx) => (
                    <div 
                      key={idx}
                      className={cn(
                        "p-3 rounded-lg border",
                        isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">{item.clause_code}</Badge>
                            <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-gray-900")}>
                              {item.clause_title}
                            </span>
                          </div>
                          <div className="flex gap-2 mt-2">
                            {item.departments?.map((dept, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{dept}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={cn("text-xl font-bold text-red-400")}>{item.occurrences}</div>
                          <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>occurrences</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Readiness Tab */}
        <TabsContent value="readiness" className="space-y-4">
          {readiness && (
            <>
              {/* Readiness Score */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className={cn("col-span-1", isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                  <CardContent className="pt-6 text-center">
                    <div className={cn(
                      "text-6xl font-bold",
                      readiness.overall_compliance_score >= 80 ? "text-emerald-400" :
                      readiness.overall_compliance_score >= 60 ? "text-amber-400" : "text-red-400"
                    )}>
                      {readiness.overall_compliance_score?.toFixed(0)}%
                    </div>
                    <p className={cn("mt-2", isDark ? "text-slate-400" : "text-gray-500")}>
                      Enterprise Readiness Score
                    </p>
                    <div className="flex items-center justify-center gap-1 mt-2">
                      {readiness.overall_trend === 'improving' ? (
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      ) : readiness.overall_trend === 'declining' ? (
                        <TrendingDown className="w-4 h-4 text-red-400" />
                      ) : (
                        <Minus className="w-4 h-4 text-slate-400" />
                      )}
                      <span className={cn("text-sm capitalize", isDark ? "text-slate-400" : "text-gray-500")}>
                        {readiness.overall_trend}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className={cn("col-span-2", isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                  <CardHeader>
                    <CardTitle className={cn("text-base", isDark ? "text-white" : "text-gray-900")}>
                      Risk Indicators
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold", readiness.total_open_findings > 0 ? "text-amber-400" : "text-emerald-400")}>
                          {readiness.total_open_findings}
                        </div>
                        <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Open Findings</p>
                      </div>
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold", readiness.overdue_capas > 0 ? "text-red-400" : "text-emerald-400")}>
                          {readiness.overdue_capas}
                        </div>
                        <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Overdue CAPAs</p>
                      </div>
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold", readiness.repeat_findings_index > 15 ? "text-red-400" : "text-emerald-400")}>
                          {readiness.repeat_findings_index?.toFixed(0)}%
                        </div>
                        <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Repeat Finding Index</p>
                      </div>
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold", readiness.systemic_weakness_count > 0 ? "text-amber-400" : "text-emerald-400")}>
                          {readiness.systemic_weakness_count}
                        </div>
                        <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Systemic Weaknesses</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Priority Actions */}
              {readiness.priority_actions?.length > 0 && (
                <Card className={cn("border-amber-500/50", isDark ? "bg-slate-800/50" : "bg-white")}>
                  <CardHeader>
                    <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                      <Zap className="w-5 h-5 text-amber-400" />
                      Priority Actions Required
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {readiness.priority_actions.map((action, idx) => (
                        <div 
                          key={idx}
                          className={cn(
                            "p-3 rounded-lg border flex items-center gap-3",
                            action.priority === 'critical' ? "border-red-500/50 bg-red-900/20" : "border-amber-500/50 bg-amber-900/20"
                          )}
                        >
                          <Badge className={action.priority === 'critical' ? 'bg-red-600' : 'bg-amber-600'}>
                            {action.priority?.toUpperCase()}
                          </Badge>
                          <span className={cn("flex-1", isDark ? "text-white" : "text-gray-900")}>
                            {action.action}
                          </span>
                          <Badge variant="outline" className="text-xs capitalize">
                            {action.category?.replace('_', ' ')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Monthly Trend */}
              {readiness.monthly_scores?.length > 0 && (
                <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                  <CardHeader>
                    <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                      Compliance Score Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={readiness.monthly_scores}>
                          <defs>
                            <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
                          <XAxis dataKey="month" stroke={isDark ? "#64748b" : "#9ca3af"} fontSize={12} />
                          <YAxis domain={[0, 100]} stroke={isDark ? "#64748b" : "#9ca3af"} fontSize={12} />
                          <RechartsTooltip 
                            contentStyle={{ 
                              backgroundColor: isDark ? '#1e293b' : '#fff', 
                              border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                              borderRadius: '8px'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="score" 
                            stroke="#10b981" 
                            fill="url(#scoreGradient)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateCampaign} onOpenChange={setShowCreateCampaign}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>
              Create Audit Campaign
            </DialogTitle>
            <DialogDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
              Group multiple audits under one enterprise campaign
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Campaign Name *</Label>
              <Input
                value={campaignForm.name}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Q1 2026 Internal Audit Campaign"
                className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Campaign Type</Label>
              <Select
                value={campaignForm.campaign_type}
                onValueChange={(v) => setCampaignForm(prev => ({ ...prev, campaign_type: v }))}
              >
                <SelectTrigger className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Audit</SelectItem>
                  <SelectItem value="external">External Audit</SelectItem>
                  <SelectItem value="regulatory">Regulatory Audit</SelectItem>
                  <SelectItem value="surveillance">Surveillance Audit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Standards to Audit (Multi-Select)</Label>
              <div className="flex flex-wrap gap-2">
                {standards.map(std => (
                  <Badge
                    key={std.id}
                    variant={campaignForm.standard_ids.includes(std.id) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer transition-colors",
                      campaignForm.standard_ids.includes(std.id) ? "bg-emerald-600" : ""
                    )}
                    onClick={() => {
                      setCampaignForm(prev => ({
                        ...prev,
                        standard_ids: prev.standard_ids.includes(std.id)
                          ? prev.standard_ids.filter(s => s !== std.id)
                          : [...prev.standard_ids, std.id]
                      }));
                    }}
                  >
                    {std.short}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Departments (Multi-Select)</Label>
              <div className="flex flex-wrap gap-2">
                {departments.map(dept => (
                  <Badge
                    key={dept}
                    variant={campaignForm.scope_departments.includes(dept) ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer transition-colors",
                      campaignForm.scope_departments.includes(dept) ? "bg-blue-600" : ""
                    )}
                    onClick={() => {
                      setCampaignForm(prev => ({
                        ...prev,
                        scope_departments: prev.scope_departments.includes(dept)
                          ? prev.scope_departments.filter(d => d !== dept)
                          : [...prev.scope_departments, dept]
                      }));
                    }}
                  >
                    {dept}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={campaignForm.is_enterprise_wide}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, is_enterprise_wide: e.target.checked }))}
                className="rounded"
              />
              <Label>Enterprise-Wide Audit (All Departments)</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCampaign(false)}>Cancel</Button>
            <Button onClick={handleCreateCampaign} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
